import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import { Action } from '@ngrx/store';

import { environment } from '../../../../environments/environment';
import {
  DeleteCookiesLoadingAdmin,
  DeleteCookiesSuccessLoadingAdmin,
  InitAuthAdmin,
  InitAuthSetAdmin,
  loginAdmin,
  loginAdminFailure,
  loginAdminSuccess,
  loginAdminSuccessCookiesSet,
  logoutAdmin,
  refreshTokenLoading,
} from './auth-admin.actions';
import { AuthService, TOKEN_EXPIRE_TIME } from '../../services/auth.service';
import { CookieCustomService } from '../../services/cookie.custom.service';
import {
  AuthUserEntityInterface,
  LoginInterface,
  ResponseLoginInterface,
} from '../../interfaces/auth.interface';
import { UserRolesEnum } from '../../interfaces/user.interface';
import { AuthCookieInterface } from './auth-admin.state';

@Injectable()
export class AuthAdminEffect {
  constructor(
    private router: Router,
    private service: AuthService,
    private actions$: Actions,
    private cookieService: CookieCustomService
  ) {}

  public loginAdmin$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(loginAdmin),
      switchMap((payload: { params: LoginInterface }) =>
        this.service.loginAdmin(payload.params).pipe(
          map((response: ResponseLoginInterface) => {
            if (response.errorCode.code !== 0) {
              throw response;
            }
            return loginAdminSuccessCookiesSet({ params: response });
          }),
          catchError((error: ResponseLoginInterface) => {
            return of(loginAdminFailure({ error: error.errorCode }));
          })
        )
      )
    );
  });

  public refreshTokenAdmin$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(refreshTokenLoading),
      switchMap((payload: { refreshToken: string }) =>
        this.service.refreshToken(payload.refreshToken).pipe(
          map((response: ResponseLoginInterface) => {
            if (response.errorCode.code !== 0) {
              throw response;
            }
            return loginAdminSuccessCookiesSet({ params: response });
          }),
          catchError((error: ResponseLoginInterface) => {
            return of(loginAdminFailure({ error: error.errorCode }));
          })
        )
      )
    );
  });

  public loginAdminSuccessCookiesSet$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(loginAdminSuccessCookiesSet),
      switchMap((payload: { params: ResponseLoginInterface }) => {
        const data = payload.params;

        if (data.errorCode.code !== 0) {
          return of(loginAdminFailure({ error: data.errorCode }));
        }

        if (data.response) {
          let response: AuthUserEntityInterface & { role?: UserRolesEnum } = {
            ...data.response,
          };
          response.role = data.response.user.role;

          let expiredAt = ((new Date().getTime() / 1000) | 0) + response.ttl;
          this.cookieService.setItem(
            environment.adminToken,
            response.id,
            expiredAt
          );
          this.cookieService.setItem(
            'entity_' + environment.adminToken,
            JSON.stringify(response)
          );
        }

        return of(loginAdminSuccess({ response: data }));
      })
    );
  });

  public authAdminInit$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(InitAuthAdmin),
      switchMap(() => {
        const entity = this.cookieService.getItem(
          'entity_' + environment.adminToken
        );
        if (entity) {
          const response: AuthCookieInterface = JSON.parse(entity);
          const expiredTime = response.ttl + response.createdAt;
          const currentTime = (new Date().getTime() / 1000) | 0;
          const refreshToken = response.refreshToken;

          if (expiredTime - TOKEN_EXPIRE_TIME < currentTime) {
            return of(refreshTokenLoading({ refreshToken }));
          } else {
            return of(InitAuthSetAdmin({ entity: response }));
          }
        } else {
          return of(InitAuthSetAdmin({ entity: null }));
        }
      })
    );
  });

  public logout$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(logoutAdmin),
      switchMap(() => {
        this.service.logoutAdmin().subscribe();
        return of(DeleteCookiesLoadingAdmin());
      })
    );
  });

  public deleteCookiesLoading$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(DeleteCookiesLoadingAdmin),
      switchMap(() => {
        this.cookieService.clearAdmin();
        return of(DeleteCookiesSuccessLoadingAdmin());
      })
    );
  });

  public deleteCookiesSuccessLoading$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(DeleteCookiesSuccessLoadingAdmin),
      switchMap(() => {
        this.router.navigate(['/']);
        return of(InitAuthAdmin());
      })
    );
  });
}
